import PropTypes from "prop-types";
import { Tooltip } from "react-tooltip";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";

import Icon from "./Icon";

const OptionsItemIcon = styled(Icon)`
    flex: 1;
    padding-right: 0.5em;
    width: 1.5em;
`;

const DropdownMenu = styled.div`
    display: flex;
    justify-content: center;
    flex-direction: column;
    position: absolute;
    background: white;
    border: 1px solid #ddd;
    border-radius: 5px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    padding: 0.5em 0;
    z-index: 1000;
`;

const DropdownItem = styled.div`
    padding: 0.5em 1em;
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
    color: ${({ disabled }) => (disabled ? "#ccc" : "inherit")};

    &:hover {
        background-color: ${({ disabled }) =>
            disabled ? "inherit" : "#f2f2f2"};
    }

    span {
        flex-grow: 1;
        white-space: nowrap;
    }
`;

const dimTestsDisabledTooltipMessage =
    "Please save your changes before starting a dim test for this group.";
const settingsDisabledTooltipMessage =
    "Please save your changes before opening the settings for this group.";

const ItemEditGeometry = ({ onEditGeometry }) => (
    <DropdownItem onClick={onEditGeometry}>
        <OptionsItemIcon className="fa fa-pencil" />
        <span>Edit Geometry</span>
    </DropdownItem>
);

ItemEditGeometry.propTypes = {
    onEditGeometry: PropTypes.func.isRequired,
};

const ItemDimTest = ({ dimTestsDisabled, onStartDimTest }) => {
    const tooltipId = uuidv4();

    return (
        <DropdownItem
            onClick={!dimTestsDisabled ? onStartDimTest : null}
            disabled={dimTestsDisabled}
            aria-disabled={dimTestsDisabled}
            data-tooltip-id={tooltipId}
            data-tooltip-content={
                dimTestsDisabled ? dimTestsDisabledTooltipMessage : null
            }
            data-tooltip-place="right"
        >
            <OptionsItemIcon className="fa fa-lightbulb" />
            <span>Start Dim Test</span>
            {dimTestsDisabled && <Tooltip id={tooltipId} />}
        </DropdownItem>
    );
};

ItemDimTest.propTypes = {
    dimTestsDisabled: PropTypes.bool.isRequired,
    onStartDimTest: PropTypes.func.isRequired,
};

const ItemSettings = ({ settingsDisabled, onOpenSettings }) => {
    const tooltipId = uuidv4();

    return (
        <DropdownItem
            onClick={!settingsDisabled ? onOpenSettings : null}
            disabled={settingsDisabled}
            aria-disabled={settingsDisabled}
            data-tooltip-id={tooltipId}
            data-tooltip-content={
                settingsDisabled ? settingsDisabledTooltipMessage : null
            }
            data-tooltip-place="right"
        >
            <OptionsItemIcon className="fa fa-cog" />
            <span>Settings</span>
            {settingsDisabled && <Tooltip id={tooltipId} />}
        </DropdownItem>
    );
};

ItemSettings.propTypes = {
    settingsDisabled: PropTypes.bool.isRequired,
    onOpenSettings: PropTypes.func.isRequired,
};

/**
 * Dropdown menu with edit geometry, dim test, and settings menu items.
 *
 * @param root0 - The component props.
 * @param root0.dropdownRef - The ref for the dropdown element.
 * @param root0.dropdownPosition - The dropdown positioning configuration.
 * @param root0.onEditGeometry - Callback to edit the group geometry.
 * @param root0.onStartDimTest - Callback to start a dim test.
 * @param root0.dimTestsDisabled - Whether dim tests are disabled.
 * @param root0.settingsDisabled - Whether settings are disabled.
 * @param root0.onOpenSettings - Callback to open the settings modal.
 * @param root0.geometryEditable - Whether the geometry can be edited.
 * @returns The rendered options menu.
 */
const OptionsMenu = ({
    dropdownRef,
    dropdownPosition,
    onEditGeometry,
    onStartDimTest,
    dimTestsDisabled,
    settingsDisabled,
    onOpenSettings,
    geometryEditable,
}) => (
    <DropdownMenu
        ref={dropdownRef}
        style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
    >
        {geometryEditable && (
            <ItemEditGeometry onEditGeometry={onEditGeometry} />
        )}
        <ItemDimTest
            dimTestsDisabled={dimTestsDisabled}
            onStartDimTest={onStartDimTest}
        />
        <ItemSettings
            settingsDisabled={settingsDisabled}
            onOpenSettings={onOpenSettings}
        />
    </DropdownMenu>
);

OptionsMenu.propTypes = {
    dropdownRef: PropTypes.any,
    dropdownPosition: PropTypes.object.isRequired,
    onEditGeometry: PropTypes.func,
    onStartDimTest: PropTypes.func,
    dimTestsDisabled: PropTypes.bool,
    settingsDisabled: PropTypes.bool,
    onOpenSettings: PropTypes.func,
    geometryEditable: PropTypes.bool,
};

export default OptionsMenu;
